import { useEffect, useState } from "react";
import { useListContext } from "ra-core";
import { ArrowDown, ArrowUp, ChevronDown, ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  emptyFilterFor,
  FilterEditor,
  isComplete,
} from "@/modules/shared/dataGrid/GridFilterMenu";
import type {
  GridColumn,
  GridFilter,
} from "@/modules/shared/dataGrid/gridTypes";

export type GridSortState = { field: string; order: "ASC" | "DESC" } | null;

/** What a header needs from useDataGrid to filter from the column itself. */
export type GridHeaderState = {
  filters: GridFilter[];
  setFilters: (filters: GridFilter[]) => void;
  /** Header menu open on this column (a chip click opens it too). */
  openColumnId: string | null;
  setOpenColumnId: (id: string | null) => void;
};

const sortFieldOf = <Row,>(column: GridColumn<Row>) =>
  column.sortField ?? column.field ?? column.id;

/**
 * Column header that opens a small menu: sort A→Z / Z→A and the column's
 * filter. Columns without sort or filter stay a plain label.
 */
const HeaderMenu = <Row,>({
  column,
  header,
  sort,
  onSort,
}: {
  column: GridColumn<Row>;
  header: GridHeaderState;
  sort: GridSortState;
  onSort: (sort: { field: string; order: "ASC" | "DESC" }) => void;
}) => {
  const field = sortFieldOf(column);
  const activeSort = sort?.field === field ? sort.order : null;
  const current = header.filters.find((f) => f.columnId === column.id) ?? null;
  const open = header.openColumnId === column.id;
  const [draft, setDraft] = useState<GridFilter | null>(null);

  useEffect(() => {
    if (open) setDraft(current ?? emptyFilterFor(column));
    // Start from the saved filter each time the menu opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => header.setOpenColumnId(null);
  const apply = () => {
    if (!draft) return close();
    const rest = header.filters.filter((f) => f.columnId !== column.id);
    header.setFilters(isComplete(draft) ? [...rest, draft] : rest);
    close();
  };

  return (
    <TableHead
      className={cn(
        "text-muted-foreground",
        column.align === "right" && "text-right",
        column.headClassName,
      )}
      aria-sort={
        activeSort
          ? activeSort === "ASC"
            ? "ascending"
            : "descending"
          : undefined
      }
    >
      <Popover
        open={open}
        onOpenChange={(next) => header.setOpenColumnId(next ? column.id : null)}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex h-8 items-center gap-1 rounded-sm text-xs font-medium uppercase tracking-wide hover:text-foreground",
              (activeSort || current) && "text-foreground",
              column.align === "right" && "ml-auto flex",
            )}
          >
            {column.label}
            {activeSort === "ASC" ? (
              <ArrowUp className="size-3 opacity-70" />
            ) : activeSort === "DESC" ? (
              <ArrowDown className="size-3 opacity-70" />
            ) : null}
            {current ? (
              <ListFilter
                className="size-3 text-primary"
                aria-label="Filtered"
              />
            ) : (
              <ChevronDown className="size-3 opacity-40" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2 normal-case">
          {column.sortable ? (
            <div className="flex flex-col">
              {(["ASC", "DESC"] as const).map((order) => (
                <Button
                  key={order}
                  type="button"
                  size="sm"
                  variant={activeSort === order ? "secondary" : "ghost"}
                  className="h-8 justify-start font-normal"
                  onClick={() => {
                    onSort({ field, order });
                    close();
                  }}
                >
                  {order === "ASC" ? (
                    <ArrowUp className="size-3.5" />
                  ) : (
                    <ArrowDown className="size-3.5" />
                  )}
                  {column.type === "date"
                    ? order === "ASC"
                      ? "Oldest first"
                      : "Newest first"
                    : column.type === "number" || column.type === "money"
                      ? order === "ASC"
                        ? "Smallest first"
                        : "Largest first"
                      : order === "ASC"
                        ? "A → Z"
                        : "Z → A"}
                </Button>
              ))}
            </div>
          ) : null}
          {column.filterable && draft ? (
            <div
              className={cn(
                "flex flex-col gap-2",
                column.sortable && "mt-2 border-t pt-2",
              )}
            >
              <p className="px-1 text-xs font-medium text-muted-foreground">
                Filter {column.label.toLowerCase()}
              </p>
              <FilterEditor
                column={column}
                filter={draft}
                onChange={setDraft}
              />
              <div className="flex justify-end gap-1 border-t pt-2">
                {current ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7"
                    onClick={() => {
                      header.setFilters(
                        header.filters.filter((f) => f.columnId !== column.id),
                      );
                      close();
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
                <Button type="button" size="sm" className="h-7" onClick={apply}>
                  Apply
                </Button>
              </div>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </TableHead>
  );
};

/** Header of a server list: sorting goes through the ra-core list. */
export const ServerGridHeaderCell = <Row,>(props: {
  column: GridColumn<Row>;
  header: GridHeaderState;
}) => {
  const { sort, setSort } = useListContext();
  return (
    <HeaderMenu
      {...props}
      sort={sort?.field ? { field: sort.field, order: sort.order } : null}
      onSort={(next) => setSort(next)}
    />
  );
};

/** Header of a table whose rows are already loaded. */
export const ClientGridHeaderCell = <Row,>(props: {
  column: GridColumn<Row>;
  header: GridHeaderState;
  sort: GridSortState;
  onSort: (sort: { field: string; order: "ASC" | "DESC" }) => void;
}) => <HeaderMenu {...props} />;
